<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { X } from 'lucide-vue-next'
import miniProgramCode from '@/assets/nova-mini-program-code.png'
import type { HomeLocale } from '@/data/videoHomeCopy'

const props = withDefaults(defineProps<{ open: boolean; locale?: HomeLocale }>(), { locale: 'zh' })
const emit = defineEmits<{ close: [] }>()
const closeButton = ref<HTMLButtonElement | null>(null)
let previousFocus: HTMLElement | null = null

const copy = computed(() => props.locale === 'en'
  ? {
      title: 'Nova Mini Program',
      instruction: 'Scan with WeChat to open the mini program.',
      imageAlt: 'Nova Mini Program code',
      close: 'Close Mini Program code',
    }
  : {
      title: 'Nova·云枢小程序',
      instruction: '请使用微信扫描小程序码进入。',
      imageAlt: 'Nova·云枢小程序码',
      close: '关闭小程序码',
    })

const close = () => emit('close')
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.open) close()
}

watch(() => props.open, async (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    await nextTick()
    closeButton.value?.focus()
  } else {
    previousFocus?.focus()
    previousFocus = null
  }
}, { immediate: true })

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="mini-program-backdrop" role="presentation" @click.self="close">
      <section class="mini-program-dialog" role="dialog" aria-modal="true" aria-labelledby="mini-program-title">
        <header>
          <div>
            <h2 id="mini-program-title">{{ copy.title }}</h2>
            <p>{{ copy.instruction }}</p>
          </div>
          <button ref="closeButton" type="button" :aria-label="copy.close" @click="close">
            <X :size="22" aria-hidden="true" />
          </button>
        </header>
        <div class="mini-program-code">
          <img :src="miniProgramCode" :alt="copy.imageAlt" />
        </div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.mini-program-backdrop {
  position: fixed;
  z-index: 220;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(3, 8, 13, .78);
  backdrop-filter: blur(18px);
}
.mini-program-dialog {
  width: min(470px, 100%);
  padding: 24px;
  color: #0a0d11;
  border: 1px solid rgba(255,255,255,.7);
  border-radius: 28px;
  background: #f7f8f6;
  box-shadow: 0 36px 110px rgba(0,0,0,.42);
}
header { display: flex; align-items: start; justify-content: space-between; gap: 24px; }
h2 { margin: 0; font-family: "Songti SC", STSong, serif; font-size: 30px; font-weight: 600; letter-spacing: -.035em; }
p { margin: 8px 0 0; color: #70777e; font-size: 14px; }
button {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  color: #0a0d11;
  border: 1px solid rgba(10,13,17,.14);
  border-radius: 50%;
  background: #fff;
}
button:hover { background: #edf8f9; }
.mini-program-code {
  margin-top: 24px;
  padding: 14px;
  overflow: hidden;
  border: 1px solid rgba(10,13,17,.1);
  border-radius: 22px;
  background: #fff;
}
img { width: 100%; height: auto; display: block; border-radius: 12px; }
@media (max-width: 560px) {
  .mini-program-backdrop { padding: 14px; }
  .mini-program-dialog { padding: 19px; border-radius: 23px; }
  h2 { font-size: 26px; }
  .mini-program-code { margin-top: 19px; padding: 10px; }
}
</style>
